import { useSeoTags } from "@/lib/seo";

/**
 * VOIHU Privacy Policy — approved verbatim text (V1.0).
 * Only the text between "APPROVED PRIVACY POLICY STARTS BELOW" and
 * "APPROVED PRIVACY POLICY ENDS HERE" (see PR spec) is rendered. Headings
 * follow the semantic hierarchy of the source (H1 → H2 → H3).
 *
 * IMPORTANT: The wording is not to be edited. Any change to the wording
 * requires a fresh approval cycle. Layout / spacing / typography may be
 * tuned; substantive text may not.
 */
export default function PrivacyPage() {
  useSeoTags({
    title: "Privacy Policy — VOIHU",
    description:
      "How VOIHU collects, uses and protects personal information. " +
      "Controller: CA HOLDING S.R.L.S. (Italy). Contact: support@voihuworld.com.",
    canonical: "https://voihuworld.com/privacy",
  });

  return (
    <main
      className="bg-[color:var(--oh-bg)] text-[color:var(--oh-ink)]"
      data-testid="privacy-page"
    >
      <article className="max-w-[720px] mx-auto px-6 sm:px-10 pt-16 pb-24">
        <div className="oh-label" data-testid="privacy-eyebrow">Legal</div>

        <h1
          className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4 text-[color:var(--oh-ink)]"
          data-testid="privacy-h1"
        >
          VOIHU Privacy Policy
        </h1>

        <p className="oh-label mt-6" data-testid="privacy-updated">
          Last updated: September 2026
        </p>

        <div
          className="mt-10 space-y-6 text-[color:var(--oh-ink)] text-base sm:text-[17px] leading-[1.75]"
          data-testid="privacy-body"
        >
          <p>
            At VOIHU, we believe that a human voice should be able to travel
            without requiring unnecessary surveillance.
          </p>
          <p>
            VOIHU is designed to collect only the information reasonably
            necessary to operate the service, enable MESSAGEs and VOICES to
            travel, process payments, protect the platform, and show how
            MESSAGEs propagate between people and places.
          </p>
          <p>
            This Privacy Policy explains what personal data we process, why we
            process it, how it is used, and the rights available to you.
          </p>

          <Section n="1" title="Who is responsible for your data">
            <p>VOIHU is operated by:</p>
            <Address>
              <div>CA HOLDING S.R.L.S.</div>
              <div>Viale Italia 8/C</div>
              <div>25126 Brescia (BS)</div>
              <div>Italy</div>
              <div>Italian Tax Code / VAT No.: 04672180983</div>
              <div>REA: BS-632332</div>
              <div>Certified Email (PEC): caholding@pec.it</div>
              <div>Service / trading name: VOIHU</div>
              <div>Website: voihuworld.com</div>
            </Address>
            <p>
              CA HOLDING S.R.L.S. is the Data Controller for the processing
              described in this Privacy Policy.
            </p>
            <p>
              Privacy and data-protection requests:{" "}
              <a
                href="mailto:support@voihuworld.com"
                className="underline underline-offset-4"
                data-testid="privacy-contact-email"
              >
                support@voihuworld.com
              </a>
            </p>
          </Section>

          <Section n="2" title="What VOIHU is">
            <p>
              VOIHU is a platform designed to allow a MESSAGE to travel from
              person to person.
            </p>
            <p>
              Users may create MESSAGEs, add their VOICE to existing MESSAGEs,
              pass MESSAGEs to other people, and observe their geographical
              propagation through VOIHU’s impact visualisations.
            </p>
            <p>VOIHU is not designed around followers or advertising profiles.</p>
            <p>
              The propagation of a MESSAGE may form a referral and participation
              chain showing how that MESSAGE travelled between participating
              users and locations.
            </p>
          </Section>

          <Section n="3" title="Personal data we process">
            <p>
              Depending on how you use VOIHU, we may process the following
              categories of information.
            </p>

            <Sub title="Account information">
              <p>When you create or access an account, we may process:</p>
              <List>
                <li>email address;</li>
                <li>display name;</li>
                <li>authentication provider;</li>
                <li>account creation and update timestamps;</li>
                <li>technical session identifiers.</li>
              </List>
              <p>VOIHU uses passwordless authentication.</p>
              <p>
                Where Google authentication is used, Google may provide basic
                account information necessary to authenticate you.
              </p>
              <p>
                VOIHU does not need your Google profile picture for the
                operation of the service and new Google authentication records
                do not retain that picture URL.
              </p>
            </Sub>

            <Sub title="MESSAGE information">
              <p>When you create a MESSAGE, we may process:</p>
              <List>
                <li>MESSAGE title;</li>
                <li>MESSAGE text;</li>
                <li>intended destination or audience;</li>
                <li>selected origin city and country;</li>
                <li>
                  geographical coordinates representing the selected city’s
                  approximate geographic centre;
                </li>
                <li>any photograph you voluntarily upload;</li>
                <li>creation and publication information.</li>
              </List>
              <p>
                New uploaded images are processed to remove embedded metadata,
                including EXIF information that may contain unnecessary
                information such as GPS data, camera information or timestamps.
              </p>
              <p>
                VOIHU does not use embedded photograph GPS information to
                determine the origin of a MESSAGE.
              </p>
            </Sub>

            <Sub title="VOICE and participation information">
              <p>When you add your VOICE to a MESSAGE, we may process:</p>
              <List>
                <li>your user identifier;</li>
                <li>the MESSAGE concerned;</li>
                <li>the city and country you explicitly select;</li>
                <li>approximate geographical coordinates for that city;</li>
                <li>
                  referral/share information showing how the MESSAGE reached
                  you;
                </li>
                <li>
                  relationships between participation and subsequent
                  participation generated through shares;
                </li>
                <li>timestamps.</li>
              </List>
              <p>
                This information is used to provide MESSAGE propagation
                features, including Global Impact and My Impact.
              </p>
            </Sub>

            <Sub title="Location information">
              <p>VOIHU does not request your device’s GPS location.</p>
              <p>You explicitly select a city through the VOIHU interface.</p>
              <p>
                Coordinates stored by VOIHU represent the approximate
                geographical centre of the selected city returned by a
                geocoding service. They do not represent your street address or
                precise physical position.
              </p>
              <p>
                VOIHU does not use a persisted IP-derived location as the
                geographic location of your VOICE.
              </p>
            </Sub>

            <Sub title="Payment information">
              <p>
                Where a VOICE or another VOIHU service requires payment,
                payments are processed by Stripe.
              </p>
              <p>VOIHU may process or retain information such as:</p>
              <List>
                <li>payment identifier;</li>
                <li>amount;</li>
                <li>currency;</li>
                <li>payment status;</li>
                <li>refund status;</li>
                <li>associated VOIHU user and MESSAGE identifiers.</li>
              </List>
              <p>
                Payment-card details entered into Stripe’s payment environment
                are processed by Stripe and are not stored by VOIHU.
              </p>
            </Sub>

            <Sub title="Reports, moderation and HEARD claims">
              <p>
                If you report content, appeal or request review of a decision,
                or use a HEARD-related feature, we may process information
                including:
              </p>
              <List>
                <li>your user identifier;</li>
                <li>email address where required;</li>
                <li>display name;</li>
                <li>report or appeal reason;</li>
                <li>information or evidence you voluntarily provide;</li>
                <li>verification or review status;</li>
                <li>
                  timestamps and information necessary to preserve the
                  integrity of the process.
                </li>
              </List>
              <p>
                Temporary verification tokens expire and are removed when no
                longer required for verification.
              </p>
              <p>
                Underlying records may be retained where reasonably necessary
                to preserve audit integrity, prevent abuse, resolve disputes or
                document actions taken in relation to a MESSAGE.
              </p>
            </Sub>

            <Sub title="Technical and first-party usage information">
              <p>
                VOIHU uses limited first-party information to operate the
                platform and understand whether MESSAGEs are opened, viewed,
                participated in and passed on.
              </p>
              <p>This may include:</p>
              <List>
                <li>MESSAGE identifier;</li>
                <li>event type;</li>
                <li>referral/share identifier;</li>
                <li>source or campaign information;</li>
                <li>random temporary session identifier;</li>
                <li>user identifier where logged in;</li>
                <li>relevant country information;</li>
                <li>timestamp.</li>
              </List>
              <p>
                VOIHU does not use this information to build advertising
                profiles or track users across unrelated websites.
              </p>
              <p>
                The VOIHU application does not persist IP addresses,
                User-Agent strings or device fingerprints as part of these
                analytics records.
              </p>
            </Sub>
          </Section>

          <Section n="4" title="Information VOIHU does not seek to collect">
            <p>
              VOIHU is designed to avoid collecting information that is not
              reasonably necessary for the service.
            </p>
            <p>
              VOIHU does not intentionally collect or persist for its normal
              application functionality:
            </p>
            <List>
              <li>device GPS position;</li>
              <li>precise street-level location;</li>
              <li>device fingerprints;</li>
              <li>advertising identifiers;</li>
              <li>browsing histories across unrelated websites;</li>
              <li>third-party advertising profiles;</li>
              <li>payment-card numbers;</li>
              <li>passwords;</li>
              <li>
                persistent IP addresses as part of VOIHU application records.
              </li>
            </List>
            <p>
              IP addresses may nevertheless be processed transiently by
              network, hosting or security infrastructure for purposes such as
              routing, abuse prevention, rate limiting and network security.
            </p>
            <p>
              Such processing does not make an IP address the geographic
              location of a user’s VOICE.
            </p>
          </Section>

          <Section n="5" title="Why we process your information">
            <p>
              VOIHU processes personal data only where an appropriate legal
              basis exists.
            </p>

            <Sub title="Providing VOIHU">
              <p>
                Account, MESSAGE, VOICE, participation, referral and location
                information may be processed where necessary to provide the
                service requested by the user.
              </p>
              <p>
                This includes creating and maintaining an account, publishing
                MESSAGEs, adding VOICES, passing MESSAGEs, maintaining
                propagation relationships and providing Global Impact, My
                Impact and HEARD functionality.
              </p>
              <p>
                The principal legal basis is performance of a contract or
                steps taken at the user’s request before entering into a
                contract.
              </p>
            </Sub>

            <Sub title="Payments and legal obligations">
              <p>
                Information may be processed to initiate, confirm, account for
                and, where applicable, refund transactions.
              </p>
              <p>
                Information may also be retained where necessary to comply
                with accounting, tax and other legal obligations.
              </p>
            </Sub>

            <Sub title="Safety, moderation and prevention of abuse">
              <p>
                VOIHU may process MESSAGE content, uploaded photographs,
                reports, referral information and relevant account information
                to identify prohibited or unlawful content, prevent abuse or
                manipulation, investigate reports, protect users and the
                platform, maintain the integrity of MESSAGE propagation and
                enforce the Terms of Service.
              </p>
              <p>
                Depending on the circumstances, this processing may be
                necessary for performance of the service, compliance with legal
                obligations, or VOIHU’s legitimate interests in operating a
                safe and trustworthy platform.
              </p>
            </Sub>

            <Sub title="Limited product analytics">
              <p>
                VOIHU processes limited first-party events to operate the
                service, prevent accidental duplicate counting and understand
                how MESSAGEs propagate.
              </p>
              <p>
                This information is not used for behavioural advertising or
                cross-site profiling.
              </p>
            </Sub>
          </Section>

          <Section n="6" title="Artificial intelligence and automated processing">
            <p>
              VOIHU uses artificial-intelligence systems to support certain
              platform functions, including MESSAGE moderation, semantic
              duplicate detection, language detection, categorisation and
              translation.
            </p>
            <p>
              Depending on the function, information submitted to these
              systems may include:
            </p>
            <List>
              <li>MESSAGE title and text;</li>
              <li>destination or intended audience;</li>
              <li>selected origin country;</li>
              <li>an uploaded image, where a MESSAGE contains one;</li>
              <li>
                limited information from existing public MESSAGEs for semantic
                duplicate detection.
              </li>
            </List>
            <p>
              VOIHU does not send your email address, session token,
              payment-card information, precise device location, referral
              history or account analytics to the AI system for these
              purposes.
            </p>

            <Sub title="Content moderation">
              <p>
                Before a new MESSAGE can be published, its content and, where
                applicable, uploaded image may be automatically analysed for
                safety and compliance with VOIHU’s content rules.
              </p>
              <p>
                The automated system may classify a proposed MESSAGE as SAFE,
                REVIEW or BLOCKED.
              </p>
              <p>
                A SAFE classification allows the publishing process to
                continue.
              </p>
              <p>
                A REVIEW classification may temporarily hold publication for
                manual review.
              </p>
              <p>
                A BLOCKED classification prevents that particular MESSAGE from
                being published.
              </p>
              <p>
                A BLOCKED or REVIEW decision does not by itself suspend or
                disable the user’s VOIHU account.
              </p>
              <p>
                Users may request human review or appeal an affected
                publication through the mechanisms provided by VOIHU.
              </p>
            </Sub>

            <Sub title="Semantic duplicate detection">
              <p>
                VOIHU is based on the principle that substantially the same
                meaning should not unnecessarily create separate MESSAGEs.
              </p>
              <p>
                Before publication, AI-supported semantic analysis may compare
                a proposed MESSAGE with a limited set of existing public
                MESSAGEs.
              </p>
              <p>
                Where the system identifies an existing MESSAGE as
                substantially identical or similar, creation of a new MESSAGE
                may be stopped and the user may instead be directed to the
                existing MESSAGE.
              </p>
              <p>
                A user who disagrees with that result may request human
                review.
              </p>
            </Sub>

            <Sub title="Language, categorisation and translation">
              <p>
                VOIHU may automatically detect the language of a MESSAGE and
                assign it to a category to organise and display content.
              </p>
              <p>
                When a viewer requests a translation, AI may generate a
                translated version of a MESSAGE.
              </p>
              <p>
                The original MESSAGE remains the authoritative source
                content.
              </p>
              <p>
                Generated translations may be cached to avoid generating the
                same translation repeatedly.
              </p>
            </Sub>

            <Sub title="Human review">
              <p>
                Automated moderation and duplicate-detection decisions may
                affect whether a particular MESSAGE can be published, but
                VOIHU provides mechanisms through which affected users may
                request human review.
              </p>
              <p>
                These processes are not used by VOIHU to create advertising
                profiles, determine creditworthiness, suspend accounts or
                make decisions concerning employment, health, insurance or
                other comparable matters.
              </p>
            </Sub>
          </Section>

          <Section n="7" title="Public information and MESSAGE propagation">
            <p>
              VOIHU is designed around the public propagation of MESSAGEs.
            </p>
            <p>
              Information intentionally provided by users may therefore become
              publicly visible, including:
            </p>
            <List>
              <li>MESSAGE title and text;</li>
              <li>uploaded photograph;</li>
              <li>selected origin city and country;</li>
              <li>cities and countries from which VOICES have been added;</li>
              <li>approximate city-centre positions;</li>
              <li>aggregate VOICE and propagation information;</li>
              <li>MESSAGE journeys and propagation paths.</li>
            </List>
            <p>
              VOIHU does not need to publicly display your email address or
              payment details as part of MESSAGE propagation.
            </p>

            <Sub title="Global Impact">
              <p>
                Global Impact represents the overall journey of a MESSAGE
                based on participation data.
              </p>
            </Sub>

            <Sub title="My Impact">
              <p>
                My Impact represents propagation attributable to a user’s
                participation and sharing activity.
              </p>
            </Sub>

            <p>
              These visualisations use approximate geographical information
              and are not intended to show an individual’s precise physical
              location.
            </p>
          </Section>

          <Section n="8" title="Sharing MESSAGEs">
            <p>
              VOIHU allows users to pass MESSAGEs through messaging
              applications, social networks, email and native device-sharing
              functionality.
            </p>
            <p>
              VOIHU does not automatically contact social networks merely
              because a user visits the platform.
            </p>
            <p>
              When you actively choose a third-party sharing service, that
              third party may process information according to its own privacy
              terms.
            </p>
            <p>
              A shared VOIHU URL may contain a referral identifier allowing
              VOIHU to understand how a MESSAGE travelled.
            </p>
            <p>
              The referral identifier is designed to represent the propagation
              relationship rather than expose the sender’s email address to
              the recipient.
            </p>
          </Section>

          <Section n="9" title="Payments">
            <p>Payments on VOIHU are processed by Stripe.</p>
            <p>
              When a payment is initiated, Stripe may process payment-card
              information and other information necessary to process the
              transaction, prevent fraud and maintain payment security.
            </p>
            <p>
              VOIHU receives information necessary to associate the
              transaction with the relevant account, MESSAGE or VOICE and
              determine whether the payment succeeded, failed or was refunded.
            </p>
            <p>VOIHU does not store full payment-card details.</p>
          </Section>

          <Section n="10" title="Geocoding">
            <p>
              When a user searches for a city, VOIHU submits the place
              query through its backend to a geocoding service.
            </p>
            <p>
              VOIHU currently uses Geoapify for user-initiated locality
              autocomplete.
            </p>
            <p>
              The geocoding service receives the place query from VOIHU’s
              server rather than directly from the user’s browser,
              together with technically necessary geocoding parameters.
              VOIHU does not intentionally forward the user’s email, user
              identifier, MESSAGE identifier, VOICE identifier, referral
              data, payment data, connection-country signal, device GPS,
              fingerprint data or end-user User-Agent to the geocoding
              service for this purpose.
            </p>
            <p>
              The location ultimately stored is the city explicitly selected
              by the user together with its country and approximate
              city-centre coordinates.
            </p>
            <p>
              VOIHU does not request device GPS location for this purpose
              and does not use Geoapify’s IP Geolocation service.
            </p>
          </Section>

          <Section n="11" title="Service providers and subprocessors">
            <p>
              VOIHU relies on specialised service providers to operate parts
              of the platform.
            </p>
            <p>
              These may provide cloud infrastructure, database, storage,
              payment, authentication, geocoding and artificial-intelligence
              services.
            </p>
            <p>Relevant providers currently include:</p>
            <List>
              <li>Stripe, for payment processing;</li>
              <li>Google, where a user chooses Google authentication;</li>
              <li>
                Geoapify, for user-initiated locality and place autocomplete;
              </li>
              <li>
                Emergent infrastructure and services, for parts of VOIHU’s
                application and AI infrastructure;
              </li>
              <li>
                Anthropic AI models, currently accessed through Emergent’s LLM
                infrastructure, for functions including moderation, semantic
                analysis, language processing, categorisation and translation.
              </li>
              <li>
                Resend, for transactional email delivery and authentication
                emails.
              </li>
              <li>
                Google Analytics 4, for optional website measurement of
                traffic and traffic sources, only when the visitor has
                accepted analytics cookies.
              </li>
            </List>
            <p>
              VOIHU seeks to provide service providers only with information
              reasonably necessary for the relevant function.
            </p>

            <Sub title="Artificial intelligence service providers">
              <p>
                Certain VOIHU functions use Anthropic AI models through
                Emergent’s LLM infrastructure.
              </p>
              <p>
                Information submitted for those functions may therefore be
                processed through Emergent infrastructure and by Anthropic as
                necessary to provide the requested functionality.
              </p>
              <p>
                Retention and processing within service-provider
                infrastructure are governed by applicable technical
                configurations, contractual arrangements and data-protection
                requirements.
              </p>
              <p>
                Because VOIHU accesses Anthropic models through intermediary
                infrastructure rather than a direct Anthropic API connection,
                VOIHU does not represent that every copy of AI-processing
                data is necessarily subject to a single provider’s standard
                retention period.
              </p>
            </Sub>

            <Sub title="Transactional Email Provider — Resend">
              <p>
                VOIHU uses Resend to send transactional and
                authentication-related emails, including one-time verification
                codes and essential service communications.
              </p>
              <p>
                For this purpose, the recipient’s email address and the
                minimum message content required for delivery may be processed
                by Resend on our behalf.
              </p>
              <p>
                VOIHU does not use Resend for advertising or behavioural
                profiling. Resend acts as a service provider supporting email
                delivery.
              </p>
            </Sub>

            <Sub title="Optional analytics — Google Analytics 4">
              <p>
                VOIHU offers optional website measurement through Google
                Analytics 4. Google Analytics 4 is used to understand overall
                website traffic, traffic sources including social platforms
                and campaigns, landing pages and device or region information
                provided by Google Analytics.
              </p>
              <p>
                Google Analytics 4 is loaded and receives data only after the
                visitor explicitly accepts analytics cookies. Advertising
                storage, ad-user-data and ad-personalization signals remain
                denied at all times. VOIHU does not use Google Analytics 4
                for advertising, remarketing or behavioural profiling.
              </p>
              <p>
                Visitors can withdraw or change their analytics preference at
                any time using the “Manage analytics preference” control on
                the Cookie Policy page.
              </p>
            </Sub>
          </Section>

          <Section n="12" title="International data transfers">
            <p>
              Some service providers used by VOIHU may process personal data
              outside Italy or the European Economic Area.
            </p>
            <p>
              Where required by applicable data-protection law, international
              transfers will be subject to an appropriate lawful transfer
              mechanism or safeguard.
            </p>
            <p>
              These may include an adequacy decision, Standard Contractual
              Clauses or another mechanism recognised under applicable law.
            </p>
          </Section>

          <Section n="13" title="Data retention">
            <p>
              VOIHU retains personal data for no longer than reasonably
              necessary for the purposes for which it was collected, subject
              to legal, accounting, security, dispute-resolution and
              platform-integrity requirements.
            </p>

            <Sub title="Sessions">
              <p>
                Authentication sessions expire after approximately 7 days.
              </p>
              <p>
                New expired session records are configured for automatic
                deletion after expiration.
              </p>
            </Sub>

            <Sub title="Temporary verification information">
              <p>
                Temporary HEARD verification tokens expire after approximately
                48 hours.
              </p>
              <p>
                Expired token material is removed, while underlying claim
                records may be retained where necessary for verification
                integrity, abuse prevention, audit or dispute handling.
              </p>
            </Sub>

            <Sub title="MESSAGEs, VOICES and propagation history">
              <p>
                MESSAGEs, VOICES and their propagation relationships form part
                of VOIHU’s core functionality and may be retained while the
                MESSAGE remains part of the service or where necessary to
                preserve legitimate propagation history.
              </p>
              <p>
                Where a valid erasure request applies, VOIHU will assess
                whether personal information can be deleted or anonymised
                while preserving legitimate non-personal or historical
                platform information.
              </p>
            </Sub>

            <Sub title="AI-generated translations and classification information">
              <p>
                Language and category information associated with a MESSAGE,
                together with cached translations, may be retained for as
                long as the underlying MESSAGE remains available on VOIHU,
                unless deletion is required earlier by law or the information
                is no longer necessary.
              </p>
            </Sub>

            <Sub title="Moderation and appeal records">
              <p>
                VOIHU may retain records relating to blocked, reviewed or
                appealed MESSAGEs where reasonably necessary to document
                moderation decisions, handle appeals, prevent abuse, resolve
                disputes or demonstrate enforcement of platform rules.
              </p>
              <p>
                Such records are not used for advertising or commercial
                profiling.
              </p>
            </Sub>

            <Sub title="Payments">
              <p>
                Transaction and accounting information may be retained for
                periods required by applicable accounting, tax and legal
                obligations.
              </p>
            </Sub>

            <Sub title="Security and disputes">
              <p>
                Relevant information may be retained where reasonably
                necessary to investigate abuse, enforce the Terms of Service,
                establish or defend legal claims, comply with legal
                obligations or resolve disputes.
              </p>
            </Sub>
          </Section>

          <Section n="14" title="Security">
            <p>
              VOIHU uses technical and organisational measures intended to
              protect personal information against unauthorised access,
              alteration, disclosure or destruction.
            </p>
            <p>Measures may include:</p>
            <List>
              <li>encrypted HTTPS communications;</li>
              <li>restricted server-side secrets;</li>
              <li>passwordless authentication;</li>
              <li>expiring sessions;</li>
              <li>payment-card isolation through Stripe;</li>
              <li>removal of unnecessary metadata from new image uploads;</li>
              <li>data minimisation;</li>
              <li>limited collection of location information;</li>
              <li>rate limiting;</li>
              <li>content moderation;</li>
              <li>restricted administrative functionality.</li>
            </List>
            <p>No internet service can guarantee absolute security.</p>
            <p>
              Where applicable law requires notification of a personal-data
              breach, VOIHU will take the actions required by that law.
            </p>
          </Section>

          <Section n="15" title="Your rights">
            <p>
              Where the GDPR or other applicable data-protection law applies,
              you may have rights including:
            </p>
            <List>
              <li>access to personal data concerning you;</li>
              <li>correction of inaccurate personal data;</li>
              <li>erasure where applicable;</li>
              <li>restriction of processing;</li>
              <li>objection to certain processing;</li>
              <li>data portability where applicable;</li>
              <li>
                withdrawal of consent where processing is based on consent;
              </li>
              <li>
                the right to lodge a complaint with a competent supervisory
                authority.
              </li>
            </List>
            <p>Requests can be submitted to:</p>
            <p>
              <a
                href="mailto:support@voihuworld.com"
                className="underline underline-offset-4"
              >
                support@voihuworld.com
              </a>
            </p>
            <p>
              VOIHU may need to verify your identity before fulfilling a
              request in order to protect your information against
              unauthorised access or deletion.
            </p>
            <p>
              Users in Italy may also lodge a complaint with the Garante per
              la protezione dei dati personali.
            </p>
          </Section>

          <Section n="16" title="Children">
            <p>
              VOIHU is not intended for use by children under the age of 16.
            </p>
            <p>
              You must be at least 16 years old to create a VOIHU account,
              create a MESSAGE or add your VOICE to a MESSAGE.
            </p>
            <p>
              VOIHU does not knowingly seek to collect personal data from
              children under 16.
            </p>
            <p>
              If we become aware that personal data has been provided by a
              child under 16 in circumstances where its processing is not
              lawful, we will take appropriate steps to remove or otherwise
              address that information.
            </p>
            <p>
              If you believe that a child under 16 has provided personal
              information to VOIHU, please contact{" "}
              <a
                href="mailto:support@voihuworld.com"
                className="underline underline-offset-4"
              >
                support@voihuworld.com
              </a>
              .
            </p>
            <p>
              Where applicable law requires a higher minimum age or additional
              parental or guardian authorisation, those requirements will
              apply.
            </p>
          </Section>

          <Section n="17" title="Cookies and similar technologies">
            <p>
              VOIHU uses limited technical and functional browser-storage
              mechanisms necessary to provide authentication and basic
              platform functionality.
            </p>
            <p>These currently include:</p>
            <List>
              <li>authentication session technology;</li>
              <li>
                local browser storage used to maintain authenticated access;
              </li>
              <li>
                temporary per-tab session storage used for limited first-party
                event deduplication and source attribution.
              </li>
            </List>
            <p>
              VOIHU does not currently use third-party advertising cookies,
              advertising pixels or third-party behavioural analytics during
              ordinary browsing.
            </p>
            <p>
              Third-party services such as Stripe may use their own security
              and technical technologies when a user actively initiates the
              relevant service, such as a payment.
            </p>
            <p>
              VOIHU will review its consent requirements before introducing
              non-essential tracking, advertising or similar technologies.
            </p>
            <p>Further information may be provided in VOIHU’s Cookie Policy.</p>
          </Section>

          <Section n="18" title="Changes to this Privacy Policy">
            <p>
              VOIHU may update this Privacy Policy as the service evolves or
              where legal, regulatory or technical changes require it.
            </p>
            <p>
              Where changes are material, additional notice may be provided
              through VOIHU or other appropriate means.
            </p>
            <p>
              The date at the beginning of this Privacy Policy indicates when
              it was last updated.
            </p>
          </Section>

          <Section n="19" title="Contact">
            <p>
              For privacy questions or to exercise data-protection rights:
            </p>
            <Address>
              <div>CA HOLDING S.R.L.S.</div>
              <div>Viale Italia 8/C</div>
              <div>25126 Brescia (BS)</div>
              <div>Italy</div>
              <div>C.F./VAT No.: 04672180983</div>
              <div>REA: BS-632332</div>
              <div>PEC: caholding@pec.it</div>
            </Address>
            <p>Privacy and data-protection requests:</p>
            <p>
              <a
                href="mailto:support@voihuworld.com"
                className="underline underline-offset-4"
              >
                support@voihuworld.com
              </a>
            </p>
          </Section>
        </div>
      </article>
    </main>
  );
}


/* ── layout helpers ─────────────────────────────────────────────────────── */

function Section({ n, title, children }) {
  return (
    <section
      className="mt-14 pt-10 border-t oh-hairline"
      data-testid={`privacy-section-${n}`}
    >
      <div className="oh-label">Section {n}</div>
      <h2 className="font-serif-editorial text-2xl sm:text-3xl leading-[1.15] mt-3 text-[color:var(--oh-ink)]">
        {title}
      </h2>
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-[color:var(--oh-ink)] mt-2 mb-3">
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function List({ children }) {
  return (
    <ul className="list-disc pl-6 space-y-2 marker:text-[color:var(--oh-ink-3)]">
      {children}
    </ul>
  );
}

function Address({ children }) {
  return (
    <address className="not-italic text-[color:var(--oh-ink)] leading-[1.75] my-2">
      {children}
    </address>
  );
}
